import React, { useState, useRef, useEffect } from 'react';
import Fields from '../Fields';
import ControlButton from '../../ControlButton';
import Button from '@input/Button';
import * as styles from './DesktopInputs.module.scss';
import Error from './error.svg';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';

export default React.memo(function DesktopInputs({
  state,
  validations,
  canValidate,
  canRefresh,
  isMobile,
  visibleErrors,
  cachedZipCodes,
  addCachedZipCode,
  inputsAreOpen,
  setState,
  cancel,
  setInputsRect,
  dotRef,
  updateErrors,
  setCanValidate,
  refreshData,
  setCurrentInput,
  prefetchScenario
}) {
  //////////////////////////
  // Layout
  //////////////////////////

  const [{ pathLength, pathStart }, setPathLength] = useState({
    pathLength: 0,
    pathStart: 39.1
  });

  // Used to disable animation when input height
  // changes due to cash out showing/hiding
  const pathLengthTimer = useRef();

  const wrapper = useRef();
  const outline = useRef();
  const dot = useRef();

  useEffect(() => {
    dot.current = dotRef.current;
  }, [dotRef]);

  useEffect(() => {
    let observer = null;

    if (!isMobile && wrapper.current && outline.current && dot.current) {
      observer = new ResizeObserver(entries => {
        const wrapperEl = wrapper?.current;
        const outlineEl = outline?.current;
        const dotEl = dot?.current;

        if (isMobile || !wrapperEl || !outlineEl || !dotEl) {
          return;
        }

        const entry = entries?.[0];
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const outlineRect = outlineEl.getBoundingClientRect();
        const dotRect = dotEl.getBoundingClientRect();

        if (entry?.contentRect) {
          clearTimeout(pathLengthTimer.current);
          outlineEl.classList.add(styles.disableAnimation);

          let { width, height, top: controlsWrapperTop, left } = wrapperRect;

          width = Math.round(width);
          height = Math.round(height);
          controlsWrapperTop = Math.round(controlsWrapperTop);
          left = Math.round(left);

          const controlsTop = controlsWrapperTop + entry.contentRect.top;

          setInputsRect(prevRect => {
            if (
              prevRect.width &&
              prevRect.height &&
              prevRect.controlsTop &&
              prevRect.left &&
              height === prevRect.height &&
              width === prevRect.width &&
              controlsTop === prevRect.controlsTop &&
              left === prevRect.left
            ) {
              return prevRect;
            }

            return { width, height, controlsTop, controlsWrapperTop, left };
          });

          setPathLength(() => {
            const dotCenter = dotRect.left + dotRect.width / 2;
            const dotOffset = dotCenter - outlineRect.left;

            pathLengthTimer.current = setTimeout(() => {
              outline.current?.classList.remove(styles.disableAnimation);
            }, 20);

            return {
              pathLength: outlineRect.width * 2 + outlineRect.height * 2,
              pathStart: (dotOffset * 100) / outlineRect.width
            };
          });
        }
      });

      observer.observe(wrapper.current);
    }

    return () => observer?.disconnect();
  }, [setInputsRect, isMobile]);

  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHasInitialized(true), 10);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      ref={wrapper}
      id="calculator-inputs"
      role="region"
      aria-hidden={isMobile ? null : !inputsAreOpen}
    >
      <div
        className={classNames(styles.inputs, {
          [styles.hidden]: !hasInitialized
        })}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <Fields
              state={state}
              canRefresh={canRefresh}
              errors={visibleErrors}
              cachedZipCodes={cachedZipCodes}
              addCachedZipCode={addCachedZipCode}
              validations={validations}
              canValidate={canValidate}
              inputsAreOpen={inputsAreOpen}
              updateErrors={updateErrors}
              setCanValidate={setCanValidate}
              setCurrentInput={setCurrentInput}
              onChange={setState}
              refresh={refreshData}
              prefetchScenario={prefetchScenario}
            />
          </div>
          <div className={styles.buttonsWrapper}>
            <ControlButton
              small
              className={classNames(styles.refresh, {
                loading: !canRefresh,
                [styles.disabled]: !canRefresh
              })}
              aria-controls="calculator-rate-table"
              disabled={!canRefresh}
              onClick={refreshData}
            >
              Refresh Mortgage Rates
            </ControlButton>
            <ValidationMessage errors={visibleErrors} />
            {!isMobile && (
              <Button
                className={classNames(styles.cancel, {
                  [styles.hidden]: !canRefresh,
                  [styles.forceShow]: visibleErrors?.length
                })}
                theme="minimal"
                icon="cancel"
                onClick={cancel}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
        {!isMobile && (
          <svg
            ref={outline}
            className={styles.outline}
            viewBox="0 0 100 100"
            aria-hidden="true"
            preserveAspectRatio="none"
          >
            <defs>
              <path
                id="dash-box-path"
                d={`M${pathStart},0H100V100H0V0H${pathStart}`}
                fill="none"
                vectorEffect="non-scaling-stroke"
              />
            </defs>
            <use
              className={styles.boxPath}
              xlinkHref="#dash-box-path"
              strokeDasharray={pathLength}
              strokeDashoffset={pathLength}
            />
            <use
              className={styles.boxDash}
              xlinkHref="#dash-box-path"
            />
          </svg>
        )}
      </div>
    </div>
  );
});

function ValidationMessage({ errors }) {
  return !errors?.length ? null : (
    <div className={styles.validation}>
      <Error /> {errors[0].message}
    </div>
  );
}
